import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Grid,
  TextField, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Chip, Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import LockIcon from "@mui/icons-material/Lock";

import {
  fetchBusinessProcess,
  deleteBusinessProcess,
  fetchValueChains,
  removeValueChainProcess,
  fetchJobs,
  fetchTasks,
  addBusinessProcessTask,
  deleteBusinessProcessTask,
  updateBusinessProcessTaskOrder,
  fetchEnterpriseDomain,
  updateBusinessProcess,
} from "../../api/companyApi";
import { COMPANY_ID } from "../../config/appConfig";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import DraggablePaper from "../../components/common/DraggablePaper";
import KpiConstraintAlert from "../../components/common/KpiConstraintAlert";
import KpiDetailDialog from "../../components/common/KpiDetailDialog";
import { useKpiUsageStatus } from "../../hooks/useKpiUsageStatus";

const EMPTY_KPI = { indicatorName: "", unit: "", measuringMethod: "", evaluationStandard: "", description: "" };

const PROCESS_TYPE_LABELS = {
  CoreProcess: "businessProcess.type.coreProcess",
  BusinessSupportProcess: "businessProcess.type.businessSupportProcess",
  EnterpriseSupportProcess: "businessProcess.type.enterpriseSupportProcess",
};

const PROCESS_CLASS_LABELS = {
  Operation: "businessProcess.class.operation",
  CustomerRelationshipManagement: "businessProcess.class.crm",
  ProductLifeCycleManagement: "businessProcess.class.plm",
  HumanResourceManagement: "businessProcess.class.hrm",
  InvestorRelationshipManagement: "businessProcess.class.irm",
  PartnerRelationshipManagement: "businessProcess.class.prm",
  DataManagement: "businessProcess.class.dm",
  ItServiceManagement: "businessProcess.class.itsm",
  AssetManagement: "businessProcess.class.am",
  Financing: "businessProcess.class.financing",
  GeneralManagement: "businessProcess.class.generalManagement",
};

export default function BusinessProcessDetailPage() {
  const { businessDomainId, businessProcessId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ── Task state ──
  const [jobs, setJobs] = useState([]);
  const [jobTasks, setJobTasks] = useState([]);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ jobId: "", taskId: "", taskOrder: 1 });
  const [taskConfirmOpen, setTaskConfirmOpen] = useState(false);
  const [taskDeleteTarget, setTaskDeleteTarget] = useState(null);

  // ── Task Edit state ──
  const [taskEditDialogOpen, setTaskEditDialogOpen] = useState(false);
  const [taskEditTarget, setTaskEditTarget] = useState(null);
  const [taskEditOrder, setTaskEditOrder] = useState(1);

  // ── KPI state ──
  const [kpiDialogOpen, setKpiDialogOpen] = useState(false);
  const [kpiEditIndex, setKpiEditIndex] = useState(null);
  const [kpiForm, setKpiForm] = useState(EMPTY_KPI);
  const [kpiDeleteIndex, setKpiDeleteIndex] = useState(null);
  const [kpiDeleteOpen, setKpiDeleteOpen] = useState(false);
  const [kpiDetailOpen, setKpiDetailOpen] = useState(false);
  const [kpiDetailRow, setKpiDetailRow] = useState(null);

  // ── Delete BP ──
  const handleDelete = async () => {
    try {
      // 削除前に全バリューチェーンから自動解除
      try {
        const valueChains = await fetchValueChains(businessDomainId);
        for (const vc of valueChains) {
          const vcData = await fetchBusinessProcess(vc.businessProcessId);
          const included = (vcData.associatedBusinessProcesses || [])
            .some(p => p.businessProcessId === businessProcessId);
          if (included) {
            await removeValueChainProcess(vc.businessProcessId, businessProcessId);
          }
        }
      } catch { /* バリューチェーン解除エラーは無視して削除を続行 */ }
      await deleteBusinessProcess(businessProcessId);
      notify.success(t("message.delete.success"));
      navigate(`/design/${businessDomainId}/business-processes`);
    } catch (e) { notify.apiError(e); }
    setConfirmOpen(false);
  };

  // ── Loaders ──
  const loadData = useCallback(async () => {
    try { setData(await fetchBusinessProcess(businessProcessId)); }
    catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessProcessId]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    (async () => {
      try {
        const domainJobs = await fetchJobs(businessDomainId);
        let allJobs = [...domainJobs];
        try {
          const enterpriseDomain = await fetchEnterpriseDomain(COMPANY_ID);
          if (enterpriseDomain && enterpriseDomain.businessDomainId !== businessDomainId) {
            const enterpriseJobs = await fetchJobs(enterpriseDomain.businessDomainId);
            const existingIds = new Set(allJobs.map((j) => j.jobId));
            enterpriseJobs.forEach((j) => { if (!existingIds.has(j.jobId)) allJobs.push(j); });
          }
        } catch (e) { /* ignore enterprise domain fetch errors */ }
        setJobs(allJobs);
      } catch (e) { /* ignore */ }
    })();
  }, [businessDomainId]);

  // ── Task CRUD ──
  const openTaskAdd = () => {
    const nextOrder = (data?.associatedTasks?.length ?? 0) + 1;
    setTaskForm({ jobId: "", taskId: "", taskOrder: nextOrder });
    setJobTasks([]);
    setTaskDialogOpen(true);
  };

  const handleJobChange = async (jobId) => {
    setTaskForm((f) => ({ ...f, jobId, taskId: "" }));
    if (!jobId) { setJobTasks([]); return; }
    try { setJobTasks(await fetchTasks(jobId)); }
    catch (e) { notify.apiError(e); }
  };

  const handleTaskAdd = async () => {
    if (!taskForm.taskId) { notify.error(t("message.error.required")); return; }
    const selectedJob = jobs.find((j) => j.jobId === taskForm.jobId);
    const selectedTask = jobTasks.find((tk) => tk.taskId === taskForm.taskId);
    const existingTasks = data?.associatedTasks || [];
    const payload = {
      companyId: COMPANY_ID,
      taskId: selectedTask.taskId,
      taskName: selectedTask.taskName,
      taskOrder: Number(taskForm.taskOrder),
      jobId: selectedTask.jobId || taskForm.jobId,
      jobName: selectedJob?.jobName || "",
    };
    try {
      await addBusinessProcessTask(businessProcessId, payload);
      notify.success(t("message.create.success"));
      setTaskDialogOpen(false);
      loadData();
    } catch (e) { notify.apiError(e); }
  };

  const handleTaskEdit = async () => {
    if (!taskEditTarget) return;
    const newOrder = Math.max(1, Number(taskEditOrder));
    try {
      // 現在のタスク一覧を taskOrder 昇順に並べ替え
      const sorted = [...(data?.associatedTasks || [])].sort((a, b) => a.taskOrder - b.taskOrder);
      // 移動対象を除いたリストに新しい位置で挿入し、連番を振り直す
      const others = sorted.filter((t) => t.taskId !== taskEditTarget.taskId);
      const insertIdx = Math.min(newOrder - 1, others.length);
      others.splice(insertIdx, 0, taskEditTarget);
      const reordered = others.map((t, i) => ({ ...t, newOrder: i + 1 }));
      // 順序が変わったタスクのみ API 呼び出し（元の taskOrder を sourceOrder として渡す）
      const originalOrderMap = {};
      sorted.forEach((t) => { originalOrderMap[t.taskId] = t.taskOrder; });
      const changes = reordered.filter((t) => originalOrderMap[t.taskId] !== t.newOrder);
      // 並行実行するとサーバーで競合書き込みが発生するため逐次実行する
      for (const t of changes) {
        await updateBusinessProcessTaskOrder(businessProcessId, t.taskId, originalOrderMap[t.taskId], t.newOrder);
      }
      notify.success(t("message.update.success"));
      setTaskEditDialogOpen(false);
      setTaskEditTarget(null);
      loadData();
    } catch (e) { notify.apiError(e); }
  };

  const handleTaskDelete = async () => {
    try {
      await deleteBusinessProcessTask(businessProcessId, taskDeleteTarget);
      notify.success(t("message.delete.success"));
      loadData();
    } catch (e) { notify.apiError(e); }
    setTaskConfirmOpen(false);
    setTaskDeleteTarget(null);
  };

  const { usedKpiNames } = useKpiUsageStatus(businessDomainId);

  // ── KPI CRUD ──
  const openKpiAdd = () => {
    setKpiEditIndex(null);
    setKpiForm(EMPTY_KPI);
    setKpiDialogOpen(true);
  };

  const openKpiEdit = (index) => {
    const ind = (data.indicators || [])[index];
    setKpiEditIndex(index);
    setKpiForm({
      indicatorName: ind.indicatorName || "",
      unit: ind.unit || "",
      measuringMethod: ind.measuringMethod || "",
      evaluationStandard: ind.evaluationStandard || "",
      description: ind.description || "",
    });
    setKpiDialogOpen(true);
  };

  const handleKpiSave = async () => {
    if (!kpiForm.indicatorName.trim() || !kpiForm.unit.trim()) {
      notify.error(t("message.error.required"));
      return;
    }
    const indicators = [...(data.indicators || [])];
    const duplicate = indicators.some((ind, i) =>
      ind.indicatorName === kpiForm.indicatorName.trim() && i !== kpiEditIndex
    );
    if (duplicate) {
      notify.error(t("businessProcessKpi.duplicate"));
      return;
    }
    const newIndicator = {
      ...kpiForm,
      indicatorName: kpiForm.indicatorName.trim(),
      unit: kpiForm.unit.trim(),
      createdAt: kpiEditIndex !== null ? indicators[kpiEditIndex].createdAt : new Date(),
    };
    if (kpiEditIndex !== null) {
      indicators[kpiEditIndex] = newIndicator;
    } else {
      indicators.push(newIndicator);
    }
    try {
      await updateBusinessProcess({ ...data, indicators });
      notify.success(kpiEditIndex !== null ? t("message.update.success") : t("message.create.success"));
      setKpiDialogOpen(false);
      loadData();
    } catch (e) { notify.apiError(e); }
  };

  const handleKpiDelete = async () => {
    const indicators = (data.indicators || []).filter((_, i) => i !== kpiDeleteIndex);
    try {
      await updateBusinessProcess({ ...data, indicators });
      notify.success(t("message.delete.success"));
      loadData();
    } catch (e) { notify.apiError(e); }
    setKpiDeleteOpen(false);
    setKpiDeleteIndex(null);
  };

  if (!data) return null;

  const associatedTasks = data.associatedTasks || [];
  const indicators = data.indicators || [];
  const hasLockedKpi = indicators.some((ind) => usedKpiNames.has(ind.indicatorName));

  const kpiRows = indicators.map((ind, i) => ({ ...ind, id: i, __index: i }));

  const kpiColumns = [
    {
      field: "indicatorName",
      headerName: t("businessProcessKpi.indicatorName"),
      flex: 1,
      minWidth: 200,
      renderCell: (p) => {
        const isLocked = usedKpiNames.has(p.row.indicatorName);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {p.row.indicatorName}
            {isLocked && <Chip icon={<LockIcon fontSize="small" />} label={t("common.inUse")} size="small" color="warning" variant="outlined" />}
          </Box>
        );
      },
    },
    { field: "unit", headerName: t("businessProcessKpi.unit"), width: 120 },
    {
      field: "actions", headerName: t("common.actions"), width: 100, sortable: false,
      renderCell: (p) => {
        const isLocked = usedKpiNames.has(p.row.indicatorName);
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title={isLocked ? t("kpi.constraint.list.info") : ""}>
              <span>
                <IconButton size="small" color="primary" disabled={isLocked} onClick={(e) => {
                  e.stopPropagation();
                  openKpiEdit(p.row.__index);
                }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={isLocked ? t("kpi.constraint.list.info") : ""}>
              <span>
                <IconButton size="small" color="error" disabled={isLocked} onClick={(e) => {
                  e.stopPropagation();
                  setKpiDeleteIndex(p.row.__index);
                  setKpiDeleteOpen(true);
                }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  const processTypeLabel = PROCESS_TYPE_LABELS[data.businessProcessType]
    ? t(PROCESS_TYPE_LABELS[data.businessProcessType])
    : data.businessProcessType || "";
  const processClassLabel = PROCESS_CLASS_LABELS[data.businessProcessClass]
    ? t(PROCESS_CLASS_LABELS[data.businessProcessClass])
    : data.businessProcessClass || "";

  // ── Column definitions ──
  const taskColumns = [
    { field: "taskOrder", headerName: t("task.order"), width: 80 },
    { field: "taskName", headerName: t("businessProcessTask.task"), flex: 1, minWidth: 180 },
    { field: "jobName", headerName: t("businessProcessTask.job"), flex: 1, minWidth: 180 },
    {
      field: "actions", headerName: t("common.actions"), width: 110, sortable: false,
      renderCell: (p) => (
        <Box>
          <IconButton size="small" color="primary" onClick={(e) => {
            e.stopPropagation();
            setTaskEditTarget(p.row);
            setTaskEditOrder(p.row.taskOrder);
            setTaskEditDialogOpen(true);
          }}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={(e) => {
            e.stopPropagation();
            setTaskDeleteTarget(p.row);
            setTaskConfirmOpen(true);
          }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      {hasLockedKpi && <KpiConstraintAlert variant="type-locked" />}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("businessProcess.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/design/${businessDomainId}/business-processes/${businessProcessId}/edit`)}
          >
            {t("common.edit")}
          </Button>
          <Tooltip title={hasLockedKpi ? t("kpi.constraint.type.locked") : ""}><span>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              disabled={hasLockedKpi}
              onClick={() => setConfirmOpen(true)}
            >
              {t("common.delete")}
            </Button>
          </span></Tooltip>
        </Box>
      </Box>

      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12} md={6}><DetailField label={t("businessProcess.name")} value={data.businessProcessName} /></Grid>
          <Grid size={12} md={3}><DetailField label={t("businessProcess.type")} value={processTypeLabel} /></Grid>
          <Grid size={12} md={3}><DetailField label={t("businessProcess.class")} value={processClassLabel} /></Grid>
          <Grid size={12}><DetailField label={t("businessProcess.description")} value={data.businessProcessDescription} /></Grid>
          <Grid size={12}><DetailField label={t("common.url")} value={data.url} /></Grid>
        </Grid>
      </DetailSection>

      {/* KPI（指標）一覧 */}
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1">{t("businessProcessKpi.list.title")}</Typography>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openKpiAdd}>
            {t("common.add")}
          </Button>
        </Box>
        <DataGrid
          rows={kpiRows}
          columns={kpiColumns}
          autoHeight
          pageSizeOptions={[5, 10]}
          initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
          disableRowSelectionOnClick
          onRowClick={(params) => { setKpiDetailRow(params.row); setKpiDetailOpen(true); }}
          sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
        />
      </Box>

      {/* Tasks */}
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1">{t("businessProcessTask.list.title")}</Typography>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openTaskAdd}>
            {t("common.add")}
          </Button>
        </Box>
        <DataGrid
          rows={associatedTasks}
          columns={taskColumns}
          getRowId={(r) => r.taskId}
          autoHeight pageSizeOptions={[5, 10]}
          initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
          disableRowSelectionOnClick
        />
      </Box>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate(`/design/${businessDomainId}/business-processes`)}>{t("common.back")}</Button>
      </Box>

      {/* Confirm Dialogs */}
      <ConfirmDialog open={confirmOpen} onConfirm={handleDelete} onCancel={() => setConfirmOpen(false)} />
      <ConfirmDialog open={taskConfirmOpen} onConfirm={handleTaskDelete} onCancel={() => { setTaskConfirmOpen(false); setTaskDeleteTarget(null); }} />
      <ConfirmDialog open={kpiDeleteOpen} onConfirm={handleKpiDelete} onCancel={() => { setKpiDeleteOpen(false); setKpiDeleteIndex(null); }} />

      {/* KPI Detail Dialog */}
      <KpiDetailDialog
        open={kpiDetailOpen}
        onClose={() => { setKpiDetailOpen(false); setKpiDetailRow(null); }}
        indicator={kpiDetailRow}
      />

      {/* KPI Add/Edit Dialog */}
      <Dialog open={kpiDialogOpen} onClose={() => setKpiDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>
          {kpiEditIndex !== null ? t("businessProcessKpi.edit.title") : t("businessProcessKpi.create.title")}
        </DialogTitle>
        <DialogContent>
          <KpiConstraintAlert
            variant={kpiEditIndex !== null
              ? (usedKpiNames.has(kpiForm.indicatorName) ? "edit-locked" : "edit-unused")
              : "create"}
            sx={{ mt: 1 }}
          />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              fullWidth required
              label={t("businessProcessKpi.indicatorName")}
              value={kpiForm.indicatorName}
              onChange={(e) => setKpiForm((f) => ({ ...f, indicatorName: e.target.value }))}
            />
            <TextField
              fullWidth required
              label={t("businessProcessKpi.unit")}
              value={kpiForm.unit}
              onChange={(e) => setKpiForm((f) => ({ ...f, unit: e.target.value }))}
            />
            <TextField
              fullWidth multiline rows={4}
              label={t("businessProcessKpi.measuringMethod")}
              value={kpiForm.measuringMethod}
              onChange={(e) => setKpiForm((f) => ({ ...f, measuringMethod: e.target.value }))}
            />
            <TextField
              fullWidth multiline rows={2}
              label={t("businessProcessKpi.evaluationStandard")}
              value={kpiForm.evaluationStandard}
              onChange={(e) => setKpiForm((f) => ({ ...f, evaluationStandard: e.target.value }))}
            />
            <TextField
              fullWidth multiline rows={2}
              label={t("businessProcessKpi.description")}
              value={kpiForm.description}
              onChange={(e) => setKpiForm((f) => ({ ...f, description: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKpiDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleKpiSave}
            disabled={!kpiForm.indicatorName.trim() || !kpiForm.unit.trim() || (kpiEditIndex !== null && usedKpiNames.has(kpiForm.indicatorName))}
          >
            {kpiEditIndex !== null ? t("common.save") : t("common.add")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task Edit Dialog */}
      <Dialog open={taskEditDialogOpen} onClose={() => setTaskEditDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("businessProcessTask.edit.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField fullWidth label={t("businessProcessTask.task")} value={taskEditTarget?.taskName || ""} disabled />
            <TextField fullWidth label={t("businessProcessTask.job")} value={taskEditTarget?.jobName || ""} disabled />
            <TextField fullWidth label={t("task.order.current")} value={taskEditTarget?.taskOrder ?? ""} disabled />
            <TextField
              fullWidth required
              type="number"
              inputProps={{ min: 1, step: 1 }}
              label={t("task.order.new")}
              value={taskEditOrder}
              onChange={(e) => setTaskEditOrder(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskEditDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleTaskEdit}>{t("common.save")}</Button>
        </DialogActions>
      </Dialog>

      {/* Task Add Dialog */}
      <Dialog open={taskDialogOpen} onClose={() => setTaskDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("businessProcessTask.add.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              required
              type="number"
              inputProps={{ min: 1, step: 1 }}
              label={t("task.order")}
              value={taskForm.taskOrder}
              onChange={(e) => setTaskForm((f) => ({ ...f, taskOrder: e.target.value }))}
            />
            <FormControl fullWidth required>
              <InputLabel>{t("businessProcessTask.job")}</InputLabel>
              <Select
                value={taskForm.jobId}
                onChange={(e) => handleJobChange(e.target.value)}
                label={t("businessProcessTask.job")}
              >
                {jobs.map((j) => (
                  <MenuItem key={j.jobId} value={j.jobId}>{j.jobName}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required disabled={!taskForm.jobId}>
              <InputLabel>{t("businessProcessTask.task")}</InputLabel>
              <Select
                value={taskForm.taskId}
                onChange={(e) => setTaskForm((f) => ({ ...f, taskId: e.target.value }))}
                label={t("businessProcessTask.task")}
              >
                {jobTasks.map((tk) => (
                  <MenuItem key={tk.taskId} value={tk.taskId}>{tk.taskName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleTaskAdd}>{t("common.add")}</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
